import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, User, LogOut, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import './Header.scss';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, isAuthenticated, logout, isLoading } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const closeUserMenu = () => {
    setIsUserMenuOpen(false);
  };

  const handleLogout = async () => {
    closeUserMenu();
    await logout();
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.header__user-menu')) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isUserMenuOpen]);

  return (
    <header className={`header ${isScrolled ? 'header--scrolled' : ''}`}>
      <div className="container">
        <div className="header__content">
          {/* Logo */}
          <Link to="/" className="header__logo" onClick={closeMenu}>
            <img src="/logo.png" alt="CoinVAT" className="header__logo-image" />
            <span className="header__logo-text">
              <span className="gradient-text">CoinVAT</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="header__nav">
            <ul className="header__nav-list">
              <li className="header__nav-item">
                <Link to="/" className="header__nav-link">
                  Home
                </Link>
              </li>
              <li className="header__nav-item">
                <Link to="https://portfolio.coinvat.io/trading/assisted" className="header__nav-link">
                  Trade
                </Link>
              </li>
              <li className="header__nav-item">
                <Link to="/features" className="header__nav-link">
                  Features
                </Link>
              </li>
              <li className="header__nav-item">
                <Link to="/about" className="header__nav-link">
                  About
                </Link>
              </li>
              <li className="header__nav-item">
                <Link to="/pricing" className="header__nav-link">
                  Pricing
                </Link>
              </li>
              <li className="header__nav-item">
                <Link to="/contact" className="header__nav-link">
                  Contact
                </Link>
              </li>
            </ul>
          </nav>

          {/* CTA Buttons */}
          <div className="header__actions">
            {isAuthenticated ? (
              <div className="header__user-menu" onClick={(e) => e.stopPropagation()}>
                <button 
                  className="header__user-button"
                  onClick={toggleUserMenu}
                >
                  <User size={20} />
                  <span className="header__user-name">
                    {user?.first_name || user?.email}
                  </span>
                  <ChevronDown size={16} />
                </button>
                {isUserMenuOpen && (
                  <div className="header__user-dropdown">
                    <Link 
                      to="/dashboard" 
                      className="header__user-menu-item"
                      onClick={closeUserMenu}
                    >
                      <User size={16} />
                      <span>Dashboard</span>
                    </Link>
                    <Link 
                      to="/settings" 
                      className="header__user-menu-item"
                      onClick={closeUserMenu}
                    >
                      <Settings size={16} />
                      <span>Settings</span>
                    </Link>
                    <hr className="header__user-menu-divider" />
                    <button 
                      className="header__user-menu-item header__user-menu-item--logout"
                      onClick={handleLogout}
                      disabled={isLoading}
                    >
                      <LogOut size={16} />
                      <span>{isLoading ? 'Signing out...' : 'Sign out'}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="btn btn-secondary btn-sm">
                  Login
                </Link>
                <Link to="/signup" className="btn btn-primary btn-sm">
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="header__menu-toggle"
            onClick={toggleMenu}
            aria-label="Toggle navigation menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`header__mobile-nav ${isMenuOpen ? 'header__mobile-nav--open' : ''}`}>
          <nav className="header__mobile-nav-content">
            <ul className="header__mobile-nav-list">
              <li className="header__mobile-nav-item">
                <Link to="/" className="header__mobile-nav-link" onClick={closeMenu}>
                  Home
                </Link>
              </li>
              <li className="header__mobile-nav-item">
                <Link to="/features" className="header__mobile-nav-link" onClick={closeMenu}>
                  Features
                </Link>
              </li>
              <li className="header__mobile-nav-item">
                <Link to="/about" className="header__mobile-nav-link" onClick={closeMenu}>
                  About
                </Link>
              </li>
              <li className="header__mobile-nav-item">
                <Link to="/pricing" className="header__mobile-nav-link" onClick={closeMenu}>
                  Pricing
                </Link>
              </li>
              <li className="header__mobile-nav-item">
                <Link to="/contact" className="header__mobile-nav-link" onClick={closeMenu}>
                  Contact
                </Link>
              </li>
            </ul>
            <div className="header__mobile-actions">
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" className="btn btn-secondary" onClick={closeMenu}>
                    Dashboard
                  </Link>
                  <button 
                    className="btn btn-outline" 
                    onClick={() => {
                      closeMenu();
                      handleLogout();
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Signing out...' : 'Sign out'}
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn btn-secondary" onClick={closeMenu}>
                    Login
                  </Link>
                  <Link to="/signup" className="btn btn-primary" onClick={closeMenu}>
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
